import { useRef } from "react";

import { useHistoricalState } from "@/Common/CustomHooks/useHistoricalState";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { IMultifactorAuthenticationUserSettings } from "@/Common/Models/UI/IMultifactorAuthenticationUserSettings";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { LoginPage as MyleResourcesForLoginPage } from "@/Common/Resources/StaticResourcesFor";
import { LoginMultifactorAuthenticationFlowStepEnum } from "@/Common/Workflow/LoginWorkflow/LoginMultifactorAuthenticationFlowStepEnum";
import { LoginMultifactorAuthenticationIsMandatoryPopup } from "./LoginMultifactorAuthenticationIsMandatoryPopup";
import { LoginMultifactorAuthenticationPopup } from "./LoginMultifactorAuthenticationPopup";
import { NewFlowMultifactorAuthenticationSetupPopup } from "./NewFlowMultifactorAuthenticationSetupPopup";

interface ILoginMultifactorAuthenticationControllerProps {
    password: string
    verifiedEmail: string | null;
}

function LoginMultifactorAuthenticationController(props: Readonly<ILoginMultifactorAuthenticationControllerProps>): JSX.Element {
    const stringProvider = useRef(new ResourceProvider(new MyleResourcesForLoginPage(), sessionStore.locale));
    const authenticationSettings: IMultifactorAuthenticationUserSettings =
    {
        IsEnabled: false,
        CodeDeliveryMethod: sessionStore.AuthenticatorCodeDeliveryMethod
    };

    const [requiredMultifactorAuthenticatorStep, previousRequiredMultifactorAuthenticatorSteps, setRequiredMultifactorAuthenticatorStep] =
    useHistoricalState<LoginMultifactorAuthenticationFlowStepEnum>(
        sessionStore.MultifactorAuthenticationSetupPromptDates?.length > 1
            ? LoginMultifactorAuthenticationFlowStepEnum.LoginMultifactorAuthenticationIsMandatoryStep
            : LoginMultifactorAuthenticationFlowStepEnum.LoginMultifactorAuthenticationStep
    );

    function backToPreviousStep(): void {
        setRequiredMultifactorAuthenticatorStep(previousRequiredMultifactorAuthenticatorSteps[0]);
    }

    async function onSaveAuthentication(setupToken: string, authenticationCode: string, codeDeliveryMethod: AuthenticatorCodeDeliveryMethod): Promise<void> {
        sessionStore.action.activateMultifactorAuthentication({ setupToken, authenticationCode, codeDeliveryMethod, password: props.password });
    }
    switch (requiredMultifactorAuthenticatorStep) {
        case LoginMultifactorAuthenticationFlowStepEnum.LoginMultifactorAuthenticationStep:
            return (
                <LoginMultifactorAuthenticationPopup
                    stringProvider={stringProvider.current}
                    onSkipMultifactorAuthenticationSetup={() => sessionStore.action.updatePromptedDateForLoginMultifactionAuthenticator()}
                    onClickMultifactorAuthenticationSetup={() => setRequiredMultifactorAuthenticatorStep(LoginMultifactorAuthenticationFlowStepEnum.LoginMultifactorAuthenticationSetupStep)}
                />
            );

        case LoginMultifactorAuthenticationFlowStepEnum.LoginMultifactorAuthenticationIsMandatoryStep:
            return (
                <LoginMultifactorAuthenticationIsMandatoryPopup
                    stringProvider={stringProvider.current}
                    onClickMultifactorAuthenticationSetup={() => setRequiredMultifactorAuthenticatorStep(LoginMultifactorAuthenticationFlowStepEnum.LoginMultifactorAuthenticationSetupStep)}
                />
            );
        case LoginMultifactorAuthenticationFlowStepEnum.LoginMultifactorAuthenticationSetupStep:
            return (
                <NewFlowMultifactorAuthenticationSetupPopup
                    authenticationSettings={authenticationSettings}
                    lastVerifiedEmail={props.verifiedEmail}
                    onSave={(setupToken: string, authenticationCode: string, codeDeliveryMethod: AuthenticatorCodeDeliveryMethod) => {
                        onSaveAuthentication(setupToken, authenticationCode, codeDeliveryMethod);
                    }}
                    onCancel={() => backToPreviousStep()}
                    maskEmail
                />
            );
    }
}

export { LoginMultifactorAuthenticationController };
